import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import iconMap from 'src/assets/images/icons/map.svg';
import iconMenu from 'src/assets/images/icons/menu.svg';
import iconStar from 'src/assets/images/icons/icon-star.svg';
import iconAction from 'src/assets/images/icons/icon-receipt-edit.svg';
import iconReceipt from 'src/assets/images/icons/receipt-purple.svg';
import iconChart from 'src/assets/images/icons/icon-presention-chart.svg';
import iconRateCard from 'src/assets/images/icons/card-edit-color.svg';
import { featureFlags } from 'src/config/featureFlags';
import { useTourContext } from 'src/presentations/guided-tours';
import { ModalConfirm } from '../../molecules/modal/Confirm';
import { AskFreyaBanner } from '../../molecules/helpCenter/AskFreyaBanner';
import { TourCard } from '../../molecules/helpCenter/TourCard';

export const GuidedTourSection: React.FC = () => {
  const { startSequence } = useTourContext();
  const navigate = useNavigate();
  const [showActionDetailModal, setShowActionDetailModal] = useState(false);

  const handleTourClick = (tourName: string) => {
    if (tourName === 'navigation') {
      startSequence('navigation-sequence');
      setTimeout(() => navigate('/'), 500);
    } else if (tourName === 'audit-exceptions') {
      startSequence('audit-sequence');
      setTimeout(() => navigate('/'), 500);
    } else if (tourName === 'action-details') {
      setShowActionDetailModal(true);
    } else if (tourName === 'performance-assessment') {
      startSequence('performance-sequence');
      setTimeout(() => navigate('/performance'), 500);
    } else if (tourName === 'reports-analytics') {
      startSequence('reports-analytics-sequence');
      setTimeout(() => navigate('/analytics'), 500);
    } else if (tourName === 'rate-card-manager') {
      startSequence('rate-card-manager-sequence');
      setTimeout(() => navigate('/ratecard-manager'), 500);
    }
  };

  const tours = [
    {
      id: 'navigation',
      title: 'Navigation',
      description: 'Learn how to operate Freya navigation',
      icon: <img src={iconMenu} className='w-24' alt='menu' />,
      iconBgClass: 'bg-green-10',
      blurColor: 'bg-[#16AD89]',
    },
    {
      id: 'audit-exceptions',
      title: 'Audit Exceptions',
      description: 'Learn how to review all audit exceptions features',
      icon: <img src={iconReceipt} className='w-24' alt='receipt' />,
      iconBgClass: 'bg-[#EEE5F2]',
      blurColor: 'bg-[#702A91]',
    },
    {
      id: 'action-details',
      title: 'Action Details',
      description: 'Review the action details of invoice items',
      icon: <img src={iconAction} className='w-24' alt='action-details' />,
      iconBgClass: 'bg-[#E8F5FC]',
      blurColor: 'bg-[#269ECC]',
    },
    {
      id: 'performance-assessment',
      title: 'Performance Assessment',
      description: 'Review the performance of all service providers',
      icon: <img src={iconStar} className='w-24' alt='performance' />,
      iconBgClass: 'bg-neutral-10',
      blurColor: 'bg-[#808080]',
    },
    {
      id: 'reports-analytics',
      title: 'Reports & Analytics',
      description: 'Explore reports and analytics features for your invoices',
      icon: <img src={iconChart} className='w-24' alt='reports-analytics' />,
      iconBgClass: 'bg-[#FEF3E2]',
      blurColor: 'bg-[#F79824]',
    },
    {
      id: 'rate-card-manager',
      title: 'Rate Card Manager',
      description: 'Manage and update rate cards for different providers',
      icon: <img src={iconRateCard} className='w-24' alt='rate-card-manager' />,
      iconBgClass: 'bg-[#EBE8FB]',
      blurColor: 'bg-[#462FBE]',
    },
  ];

  // Dynamic grid columns based on number of items
  const getGridCols = () => {
    if (tours.length === 1) return 'grid-cols-1';
    if (tours.length === 2) return 'grid-cols-1 md:grid-cols-2';
    return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3';
  };

  return (
    <div className='w-full'>
      <div className='flex items-center gap-8'>
        <img src={iconMap} alt='search' className='w-24 h-24' />
        <h2 className='text-20 font-bold text-neutral-80 m-0'>Guided Tours</h2>
      </div>
      <p className='text-16 text-neutral-60 font-normal leading-relaxed'>
        Choose a tour to learn about specific features. Each tour walks you through step by step.
      </p>

      <div className={`grid ${getGridCols()} gap-24 mt-24`}>
        {tours.map((tour) => (
          <TourCard
            key={tour.id}
            title={tour.title}
            description={tour.description}
            icon={tour.icon}
            iconBgClass={tour.iconBgClass}
            blurColor={tour.blurColor}
            onClick={() => handleTourClick(tour.id)}
          />
        ))}
      </div>

      {featureFlags.SHOW_ASK_FREYA && (
        <div className='mt-32'>
          <AskFreyaBanner />
        </div>
      )}

      <ModalConfirm
        open={showActionDetailModal}
        title='Action Details Tour'
        buttonCancelLabel='Cancel'
        buttonAcceptLabel='Go to Invoices'
        onCancel={() => setShowActionDetailModal(false)}
        onAccept={() => {
          setShowActionDetailModal(false);
          startSequence('action-details-sequence');
          setTimeout(() => navigate('/'), 300);
        }}
      >
        <div className='flex flex-col gap-16'>
          <div className='flex items-center gap-12 p-16 bg-neutral-10 rounded-12'>
            <img src={iconAction} className='w-32 h-32 flex-shrink-0' alt='action-details' />
            <p className='text-14 text-neutral-70 leading-relaxed'>
              The Action Details tour walks you through the invoice review interface — deviations, approvals, charge lines, and more.
            </p>
          </div>
          <p className='text-14 text-neutral-60 leading-relaxed'>
            To get started, open any invoice from the <strong className='text-neutral-80'>Audit Exceptions</strong> page. The tour will begin automatically once
            you&apos;re inside an invoice.
          </p>
        </div>
      </ModalConfirm>
    </div>
  );
};
